import { ArrowUp, CircleStop, Mic, Paperclip } from 'lucide-react'
import * as React from 'react'

import { DictationStatus, useDictation } from '@/hooks/useDictation'
import { cn } from '../../utils/cn'
import { Button } from './button'
import { DictationBar } from './dictation-bar'
import { FileChip } from './file-chip'
import { FileDropOverlay } from './FileDropOverlay'
import { Input } from './input'
import { Textarea } from './textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'

export type AgentComposerFile = React.ComponentProps<typeof FileChip> & { key: string }

type AgentComposerProps = Omit<React.ComponentProps<'form'>, 'onSubmit'> & {
  value: string
  onValueChange: (value: string) => void
  onSend: () => void
  /** Disables the send button while the box stays editable, e.g. with nothing to send. */
  sendDisabled?: boolean
  /** Disables typing, attaching and sending, e.g. before the conversation loads. */
  disabled?: boolean
  placeholder?: string
  maxLength?: number
  textareaRef?: React.Ref<HTMLTextAreaElement>
  /** Shows Stop in place of send while an answer streams in. */
  onStop?: () => void
  stopping?: boolean
  /** Shows the paperclip; omit to hide attaching. */
  onAttach?: (files: File[]) => void
  accept?: string
  files?: AgentComposerFile[]
  /** A file is dragged over the drop zone. */
  dragging?: boolean
  /** Shows the microphone, which adds what was said to the message. */
  dictation?: boolean
}

/** The agent chat's message box: files, paperclip, text, microphone and send or stop. */
export function AgentComposer({
  value,
  onValueChange,
  onSend,
  sendDisabled,
  disabled,
  placeholder = 'Message Cai',
  maxLength,
  textareaRef,
  onStop,
  stopping,
  onAttach,
  accept,
  files = [],
  dragging,
  dictation: dictationEnabled,
  className,
  ...props
}: AgentComposerProps) {
  const filePicker = React.useRef<HTMLInputElement>(null)
  const form = React.useRef<HTMLFormElement>(null)
  // What was said lands after a pause for the model, so it is added to the message as it is by then.
  const latestValue = React.useRef(value)
  React.useEffect(() => {
    latestValue.current = value
  })
  const dictation = useDictation((text) => {
    const current = latestValue.current
    const message = current.trim() ? `${current.trimEnd()} ${text}` : text
    onValueChange(maxLength === undefined ? message : message.slice(0, maxLength))
  })
  const dictating = dictation.status !== DictationStatus.IDLE
  // However a dictation ends, the message box takes the focus back, unless the focus has moved somewhere else.
  const wasDictating = React.useRef(false)
  React.useEffect(() => {
    const ended = wasDictating.current && !dictating
    wasDictating.current = dictating
    const textarea = form.current?.querySelector('textarea')
    const focus = document.activeElement
    if (!ended || !textarea || (focus && focus !== document.body && !form.current?.contains(focus))) return
    textarea.focus()
    // Ready to keep writing after what was said.
    textarea.setSelectionRange(textarea.value.length, textarea.value.length)
  }, [dictating])
  const { cancel: cancelDictation } = dictation
  // The microphone stops when the box can't be seen: the chat stays mounted behind another tab or slid out of view,
  // and the browser tab can be switched away from.
  React.useEffect(() => {
    const element = form.current
    if (!dictating || !element) return
    const stopWhenHidden = () => {
      if (document.hidden) cancelDictation()
    }
    document.addEventListener('visibilitychange', stopWhenHidden)
    const observer =
      typeof IntersectionObserver === 'function'
        ? new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) cancelDictation()
          })
        : null
    observer?.observe(element)
    return () => {
      document.removeEventListener('visibilitychange', stopWhenHidden)
      observer?.disconnect()
    }
  }, [dictating, cancelDictation])

  return (
    <form
      ref={form}
      data-slot="agent-composer"
      className={cn(
        'has-[textarea:focus-visible]:border-ring bg-neutral-0 border-input hover:border-ink-300 relative flex flex-col gap-2 rounded-2xl border p-2 transition-colors',
        className
      )}
      onSubmit={(event) => {
        event.preventDefault()
        onSend()
      }}
      {...props}
    >
      {dragging && <FileDropOverlay className="inset-0 rounded-2xl [&_svg]:size-7" />}
      {files.length > 0 && (
        <ul className="m-0 flex list-none flex-wrap gap-2 px-1 pt-1" aria-label="Files to send">
          {files.map(({ key, ...chip }) => (
            <li key={key}>
              <FileChip {...chip} />
            </li>
          ))}
        </ul>
      )}
      <div className={cn('flex gap-2', dictating ? 'min-h-11 items-center' : 'items-end')}>
        {dictating && <DictationBar dictation={dictation} />}
        {/* Hidden rather than removed while dictating, so the draft keeps its height. */}
        <div className={cn('contents', dictating && 'hidden')}>
          {onAttach && (
            <>
              <Input
                ref={filePicker}
                className="hidden"
                type="file"
                accept={accept}
                multiple
                aria-label="Files to attach"
                tabIndex={-1}
                onChange={(event) => {
                  onAttach(Array.from(event.target.files ?? []))
                  // Cleared so choosing the same file again, after removing it, still fires a change.
                  event.target.value = ''
                }}
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    type="button"
                    className="text-ink-400 hover:text-ink mb-1 rounded-full"
                    aria-label="Attach a file"
                    disabled={disabled}
                    onClick={() => filePicker.current?.click()}
                  >
                    <Paperclip aria-hidden="true" className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Attach a PDF, spreadsheet or photo</TooltipContent>
              </Tooltip>
            </>
          )}
          <Textarea
            className="text-ink-900 field-sizing-fixed min-h-10 min-w-0 flex-1 rounded-none border-0 bg-transparent px-0 py-2 text-base leading-6 shadow-none focus-visible:outline-none! dark:bg-transparent md:pointer-fine:text-base"
            ref={textareaRef}
            aria-label={placeholder}
            placeholder={placeholder}
            rows={1}
            maxLength={maxLength}
            value={value}
            disabled={disabled}
            onChange={(event) => onValueChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault()
                if (!disabled && !sendDisabled) onSend()
              }
            }}
          />
          {dictationEnabled && dictation.supported && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  type="button"
                  className="text-ink-400 hover:text-ink mb-1 size-8 rounded-full"
                  aria-label="Dictate"
                  disabled={disabled}
                  onClick={() => void dictation.start()}
                >
                  <Mic aria-hidden="true" className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Dictate</TooltipContent>
            </Tooltip>
          )}
        </div>
        {/* Stop stays while dictating, so an answer can be stopped without giving up what is being said. */}
        {onStop ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                type="button"
                className={cn('size-8 rounded-full', !dictating && 'mb-1')}
                aria-label={stopping ? 'Stopping' : 'Stop'}
                loading={stopping ? <span className="sr-only">Stopping…</span> : undefined}
                onClick={onStop}
              >
                <CircleStop aria-hidden="true" className="size-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{stopping ? 'Stopping…' : 'Stop response'}</TooltipContent>
          </Tooltip>
        ) : (
          !dictating && (
            <Button
              size="icon"
              type="submit"
              className="mb-1 size-8 rounded-full"
              aria-label="Send message"
              disabled={disabled || sendDisabled}
            >
              <ArrowUp aria-hidden="true" />
            </Button>
          )
        )}
      </div>
      {dictation.error && (
        <p className="text-destructive px-1 text-sm" role="alert">
          {dictation.error}
        </p>
      )}
    </form>
  )
}
